// frontend/src/features/ups/modals/CreateSiteModal.js
// UPS Admin: Create Site modal
// - POST /ups-admin/sites
// - Validates code format client-side to match backend rules:
//   ^[A-Z0-9_-]{2,12}$

import React, { useMemo, useState } from 'react';
import axios from 'axios';

const CODE_REGEX = /^[A-Z0-9_-]{2,12}$/;
const inputClass =
  'w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/20';
const labelClass = 'mb-1 block text-sm font-medium text-app-muted';
const primaryButtonClass =
  'rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60';
const secondaryButtonClass =
  'rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:cursor-not-allowed disabled:opacity-60';

export default function CreateSiteModal({ isOpen, onClose, onCreated }) {
  const siteNameId = 'create-site-name';
  const siteCodeId = 'create-site-code';
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const normalized = useMemo(() => {
    return {
      name: (name ?? '').trim(),
      code: (code ?? '').trim().toUpperCase(),
    };
  }, [name, code]);

  if (!isOpen) return null;

  const validate = () => {
    if (!normalized.name) return 'Site name is required';
    if (!normalized.code) return 'Site code is required';
    if (!CODE_REGEX.test(normalized.code)) {
      return 'Code must be 2–12 chars: A–Z, 0–9, underscore, or dash (e.g. MAIN, DEPOT_1, SITE-2)';
    }
    return '';
  };

  const submit = async () => {
    setError('');
    const msg = validate();
    if (msg) return setError(msg);

    setSaving(true);
    try {
      const res = await axios.post('/ups-admin/sites', {
        name: normalized.name,
        code: normalized.code,
      });

      // Support either { site } or direct site
      const site = res.data?.site ?? res.data;
      onCreated?.(site);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to create site');
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    if (saving) return;
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={close}
    >
      <div
        className="flex h-[78dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-app-border bg-app-card text-app-text shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-app-border px-4 py-4 sm:px-5">
          <h2 className="text-lg font-semibold text-app-text">Create Site</h2>
          <button
            type="button"
            onClick={close}
            disabled={saving}
            aria-label="Close create site modal"
            className="rounded-xl px-3 py-2 text-app-muted transition hover:bg-app-surface hover:text-app-text disabled:cursor-not-allowed disabled:opacity-60"
          >
            x
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor={siteNameId} className={labelClass}>Site name</label>
              <input
                id={siteNameId}
                name="siteName"
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Main Depot"
                disabled={saving}
              />
            </div>

            <div>
              <label htmlFor={siteCodeId} className={labelClass}>Code</label>
              <input
                id={siteCodeId}
                name="siteCode"
                className={`${inputClass} font-mono`}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="MAIN"
                disabled={saving}
              />
              <div className="mt-1 text-xs text-app-muted">
                Format: 2–12 chars, uppercase A–Z/0–9, underscore, dash. Example: <span className="font-mono">DEPOT_1</span>
              </div>
            </div>
          </div>
        </div>

        <div
          className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-app-border bg-app-surface px-4 py-3 sm:px-5 sm:py-4"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={close}
            className={secondaryButtonClass}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className={primaryButtonClass}
            disabled={saving}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
